import { getLedgerSafeOrderedUnits } from './ledgerSafeBoundaryReconcile'
import type { AnchoredRow } from '../types/anchoredRowTypes'
import type {
  ChapterCharacterLayer,
  CharacterOccurrenceLocator,
} from '../types/characterLayerTypes'
import type { CompleteMicroSegmentationResult, MicroUnit } from '../types/segmentationTypes'

type RebaseStatus = 'rebased' | 'recovered-by-text' | 'unresolved-locator' | 'unresolved-cross-boundary'

interface RebaseProvenance {
  status: RebaseStatus
  absoluteStart?: number
  absoluteEnd?: number
  from: CharacterOccurrenceLocator
  to?: CharacterOccurrenceLocator
}

interface LocatorRebaseResult {
  locator: CharacterOccurrenceLocator
  provenance?: RebaseProvenance
  sourceText?: string
}

const unitId = (unit: MicroUnit): string => String(unit.unit_uuid || unit.unit_id || '').trim()

const activeUnitIdByRow = (rows: AnchoredRow[], witnessId: string): Map<string, string> => {
  const result = new Map<string, string>()
  rows.forEach(row => {
    const mapping = row.unitMappings.find(item => item.witnessId === witnessId && !item.excluded)
    if (mapping?.unitUuid) {
      result.set(row.id, mapping.unitUuid)
    }
  })
  return result
}

const activeRowByUnitId = (rows: AnchoredRow[], witnessId: string): Map<string, string> => {
  const result = new Map<string, string>()
  rows.forEach(row => {
    const mapping = row.unitMappings.find(item => item.witnessId === witnessId && !item.excluded)
    if (mapping?.unitUuid) {
      result.set(mapping.unitUuid, row.id)
    }
  })
  return result
}

const finiteOffsets = (locator: CharacterOccurrenceLocator): { start: number; end: number } | null => {
  const start = Number(locator.localStart)
  const end = Number(locator.localEnd)
  return Number.isFinite(start) && Number.isFinite(end) && start >= 0 && end >= start
    ? { start, end }
    : null
}

const quoteCandidates = ({
  quote,
  units,
  rowByUnitId,
  witnessId,
}: {
  quote: string
  units: MicroUnit[]
  rowByUnitId: Map<string, string>
  witnessId: string
}): CharacterOccurrenceLocator[] => {
  const normalizedQuote = quote.trim()
  if (!normalizedQuote) {
    return []
  }
  const matches: CharacterOccurrenceLocator[] = []
  units.forEach(unit => {
    const rowId = rowByUnitId.get(unitId(unit))
    if (!rowId) {
      return
    }
    const text = String(unit.unit_text_content || '')
    let cursor = text.indexOf(normalizedQuote)
    while (cursor >= 0) {
      matches.push({
        witnessId,
        rowId,
        localStart: cursor,
        localEnd: cursor + normalizedQuote.length,
      })
      cursor = text.indexOf(normalizedQuote, cursor + 1)
    }
  })
  return matches
}

const sameLocator = (left: CharacterOccurrenceLocator, right: CharacterOccurrenceLocator): boolean => (
  left.witnessId === right.witnessId &&
  left.rowId === right.rowId &&
  left.localStart === right.localStart &&
  left.localEnd === right.localEnd
)

const rebaseLocator = ({
  locator,
  quote,
  previousUnitByRow,
  previousUnitById,
  nextUnits,
  nextRowByUnitId,
}: {
  locator: CharacterOccurrenceLocator
  quote: string
  previousUnitByRow: Map<string, string>
  previousUnitById: Map<string, MicroUnit>
  nextUnits: MicroUnit[]
  nextRowByUnitId: Map<string, string>
}): LocatorRebaseResult => {
  const offsets = finiteOffsets(locator)
  const previousUnit = previousUnitById.get(previousUnitByRow.get(locator.rowId) || '')
  if (!offsets || !previousUnit) {
    return {
      locator,
      provenance: { status: 'unresolved-locator', from: { ...locator } },
    }
  }

  const absoluteStart = Number(previousUnit.start_position) + offsets.start
  const absoluteEnd = Number(previousUnit.start_position) + offsets.end
  const containingUnit = nextUnits.find(unit => {
    const start = Number(unit.start_position)
    const end = Number(unit.end_position)
    return Number.isFinite(start) && Number.isFinite(end) && absoluteStart >= start && absoluteEnd <= end
  })
  const containingRowId = containingUnit ? nextRowByUnitId.get(unitId(containingUnit)) : undefined

  if (containingUnit && containingRowId) {
    const nextStart = Number(containingUnit.start_position)
    const nextLocator: CharacterOccurrenceLocator = {
      witnessId: locator.witnessId,
      rowId: containingRowId,
      localStart: absoluteStart - nextStart,
      localEnd: absoluteEnd - nextStart,
    }
    if (sameLocator(locator, nextLocator)) {
      return { locator }
    }
    return {
      locator: nextLocator,
      sourceText: String(containingUnit.unit_text_content || '').slice(nextLocator.localStart, nextLocator.localEnd),
      provenance: {
        status: 'rebased',
        absoluteStart,
        absoluteEnd,
        from: { ...locator },
        to: nextLocator,
      },
    }
  }

  const recovered = quoteCandidates({
    quote,
    units: nextUnits,
    rowByUnitId: nextRowByUnitId,
    witnessId: locator.witnessId,
  })
  if (recovered.length === 1) {
    return {
      locator: recovered[0],
      sourceText: quote.trim(),
      provenance: {
        status: 'recovered-by-text',
        absoluteStart,
        absoluteEnd,
        from: { ...locator },
        to: recovered[0],
      },
    }
  }

  return {
    locator,
    provenance: {
      status: 'unresolved-cross-boundary',
      absoluteStart,
      absoluteEnd,
      from: { ...locator },
    },
  }
}

const noteQuote = (metadata: Record<string, any> | undefined): string => {
  for (const key of ['span', 'scope', 'marker']) {
    const value = metadata?.[key]
    if (typeof value === 'string' && value.trim()) {
      return value
    }
  }
  return ''
}

export const rebaseCharacterLayerAfterSegmentationEdit = ({
  layer,
  witnessId,
  previousSegmentationResult,
  nextSegmentationResult,
  previousRows,
  nextRows,
  timestamp = new Date().toISOString(),
}: {
  layer: ChapterCharacterLayer
  witnessId: string
  previousSegmentationResult: CompleteMicroSegmentationResult | null | undefined
  nextSegmentationResult: CompleteMicroSegmentationResult | null | undefined
  previousRows: AnchoredRow[]
  nextRows: AnchoredRow[]
  timestamp?: string
}): ChapterCharacterLayer => {
  if (!witnessId || !previousSegmentationResult || !nextSegmentationResult) {
    return layer
  }

  const previousUnits = getLedgerSafeOrderedUnits(previousSegmentationResult).map(entry => entry.unit)
  const nextUnits = getLedgerSafeOrderedUnits(nextSegmentationResult).map(entry => entry.unit)
  const previousUnitById = new Map(previousUnits.map(unit => [unitId(unit), unit]))
  const previousUnitByRow = activeUnitIdByRow(previousRows, witnessId)
  const nextRowByUnitId = activeRowByUnitId(nextRows, witnessId)
  let changed = false

  const occurrences = layer.occurrences.map(occurrence => {
    if (occurrence.locator.witnessId !== witnessId) {
      return occurrence
    }
    const result = rebaseLocator({
      locator: occurrence.locator,
      quote: occurrence.label || '',
      previousUnitByRow,
      previousUnitById,
      nextUnits,
      nextRowByUnitId,
    })
    if (!result.provenance) {
      return occurrence
    }
    changed = true
    return {
      ...occurrence,
      locator: result.locator,
      label: result.sourceText || occurrence.label,
      metadata: { ...occurrence.metadata, segmentation_rebase: result.provenance },
      updated_at: timestamp,
    }
  })

  const notes = layer.notes.map(note => {
    if (note.anchor.kind !== 'span' || note.anchor.locator.witnessId !== witnessId) {
      return note
    }
    const result = rebaseLocator({
      locator: note.anchor.locator,
      quote: noteQuote(note.metadata),
      previousUnitByRow,
      previousUnitById,
      nextUnits,
      nextRowByUnitId,
    })
    if (!result.provenance) {
      return note
    }
    changed = true
    const metadata: Record<string, any> = { ...note.metadata, segmentation_rebase: result.provenance }
    const sourceField = typeof metadata.source_field === 'string' ? metadata.source_field : 'span'
    if (result.sourceText && ['span', 'scope', 'marker'].includes(sourceField)) {
      metadata[sourceField] = result.sourceText
    }
    return {
      ...note,
      anchor: { kind: 'span' as const, locator: result.locator },
      metadata,
      updated_at: timestamp,
    }
  })

  return changed ? {
    ...layer,
    version: layer.version + 1,
    occurrences,
    notes,
    updated_at: timestamp,
  } : layer
}
